import { Trans } from '@lingui/macro'
import { Box, Breadcrumbs, Container, Tab, Tabs } from '@mui/material'
import { SxProps, Theme } from '@mui/system'
import { Routes } from '@om1/platform-routing'
import { BreadcrumbLink } from '@om1/platform-ui-kit/src/components/Layout'
import React, { useEffect, useState } from 'react'
import ReactMarkdown from 'react-markdown'
import { useLocation } from 'react-router-dom'

// RWDC content for the Real World Data Cloud section
// eslint-disable-next-line string-to-lingui/missing-lingui-transformation
const rwdcGettingStartedContent = `# Real World Data Cloud

## Overview

The OM1 Real World Data Cloud (RWDC) is a normalized, research-ready, continuously updated dataset covering over 350 million de-identified US patient lives starting in 2013. The data consists of electronic medical record (EMR) data pre-linked with medical and pharmacy claims and enriched using medical language processing and AI.

![RWDC Overview](/img/rwdc_overview.png)

## Use Cases

* **Real-World Evidence Studies:** Generate evidence on treatment effectiveness and safety
* **Patient Journey Analysis:** Understand complete patient pathways across the healthcare system
* **Comparative Effectiveness Research:** Compare treatments in real-world settings
* **Healthcare Outcomes Research:** Analyze patient outcomes and quality measures
* **Population Health Studies:** Study disease prevalence and treatment patterns at scale`

// eslint-disable-next-line string-to-lingui/missing-lingui-transformation
const rwdcDataSourcesContent = `# Data Sources

## Clinical Data (EMR)
* Electronic medical record data sourced from providers and practices within the OM1 Specialty Networks
* Medication and prescription information
* Laboratory results and vital signs
* Patient-reported outcomes (PROs)
* Diagnoses across various healthcare settings
* Unstructured physician notes (processed using medical language processing and AI)

## Claims Data
* Medical and pharmacy claims data
* Information on procedures and administered medications
* Encounters from various care settings
* "Open" claims reflecting what was billed (does not guarantee all insurance claims were captured)
* Does not include final paid amounts or patient out-of-pocket expenses

## Supplementary Data
* **Provider Data:** Provider specialty, location, and other attributes
* **Medication Reference Data:** Standardized RxNorm reference information for medication records
* **Mortality Data:** Month and year of death
* **Social Determinants of Health (SDoH):** Race, ethnicity, income level, education status, and other factors`

// eslint-disable-next-line string-to-lingui/missing-lingui-transformation
const rwdcDataCharacteristicsContent = `# Data Characteristics

## Coverage & Scale
* **Longitudinal:** Patient journeys starting from 2013
* **Multi-Setting:** Data spans various healthcare settings and provider types
* **350+ Million Patient Lives:** Comprehensive coverage of US patient population


## Quality & Processing

The RWDC undergoes rigorous processing to ensure research readiness:

* **Normalization:** All data is standardized using common healthcare ontologies
* **De-identification:** Patient data is fully de-identified to protect patient privacy
* **Linkage:** Data is linked between EMR, claims and supplementary data at the patient level
* **Enrichment:** Medical language processing and AI are used to extract valuable clinical outcomes from unstructured notes
* **Data Quality:** Data is validated and cleaned to ensure accuracy and consistency
* **Continuously Updated:** Datasets are updated quarterly`

// Separate markdown content for each Insights Platform tab
// eslint-disable-next-line string-to-lingui/missing-lingui-transformation
const gettingStartedContent = `# Insights Platform

## Overview

The OM1 Insights Platform is designed to help customers discover real-world data and insights that are specifically tailored to their needs ("fit for purpose"). It is built on top of the OM1 Real World Data Cloud (RWDC)–a research ready dataset of 350M+ real-world patient journeys.

The platform includes three main applications–Cohort Explorer, Insights Library and Data Studio–that together can be used to discover, evaluate, gain insights and deeply analyze real-world data. 

![Platform Overview](/img/insights_platform_overview.png)

## Use Cases

* **Data Exploration:** Explore all pre-built datasets including the RWDC, OM1 Specialty Networks and PremiOM datasets. View and explore custom datasets built by users within your organization. 

* **Cohort Building and Feasibility Assessments:** Develop cohorts that fit your specific needs by applying inclusion and exclusion criteria using patient demographics, diagnoses, medications, observations and other factors. Combine criteria into AND, OR and relate statements to create more advanced cohorts. Assess feasibility of real-world clinical studies or clinical trial recruitment.

* **Rapid Insights Generation:** Rapidly generate insights on comparative effectiveness, reasons for discontinuation and provider prescribing patterns. Receive monthly refreshes to track trends over time and monitor change. 

* **Interactive Data Analysis:** Deliver datasets to an interactive data analysis environment to further evaluate datasets and/ or perform your full analysis.`

// eslint-disable-next-line string-to-lingui/missing-lingui-transformation
const cohortExplorerContent = `# Cohort Explorer

## Overview

Cohort Explorer is a powerful tool that enables you to easily understand and assess large real-world datasets through a simple web interface. 

### Use Cases

* **View All Available Datasets:** View all pre-built datasets including the RWDC, OM1 Specialty Networks and PremiOM datasets. View custom datasets built by users within your organization. 

* **Create Custom or Fit-for-Purpose Cohorts:** Develop cohorts that fit your specific needs by applying inclusion and exclusion criteria using patient demographics, diagnoses, medications, observations, lab results and observation periods (proxy for enrollment). Combine criteria into AND, OR and relate statements to create more advanced cohorts.  

* **View High-Level Dataset Counts:** View counts of patients with any data element, including patient demographics, types of data (e.g. EHR, claims, etc), diagnosis, medications, observations, procedures and labs for any pre-built or custom dataset.

* **Identify Dataset For Delivery:** Datasets can be delivered to OM1 Data Studio for deeper data evaluation and analysis, to Insights Library for additional feasibility and continuous analytics, and/ or externally to your data lake. 

## Workflows

### [Dataset Reports](/cohort-builder/dashboard/dataset/ce3a7886-d416-4681-b38f-84051280b061/demographics)

View high-level dataset counts for pre-built and custom datasets by exploring the tabs in the dashboard view. 

You can also export a report of these counts by clicking the "Generate Report" icon at top right.  

![Dataset Reports](/img/explorer-reports.png)

### [Custom Cohort Creation](/cohort-builder/dashboard/custom/)

**1. Create a New Cohort**

* Click the "Create Cohort" button.

* Enter a Name for your cohort and select your initial dataset, which can be the OM1 Real-World Data Cloud, an OM1 Specialty Network or a PremiOM dataset.

* Click "Standard Build" or "No thanks, I'll build my cohort manually" to proceed. If "Standard Build" is selected, it will take you through a wizard to select diagnoses, medications and outcomes that are relevant for your cohort or study. It will also add a criteria that requires each patient has EHR and claims data. 

![Cohort Creation](/img/explorer-create_cohort.png)

**2. Define Your Cohort** 

On the cohort editing page, you can build inclusion or exclusion criteria blocks using the following elements: 

* Patient Attributes   
* Diagnoses  
* Medications  
* Observations  
* Procedures  
* Labs  
* EHR Notes

![Cohort Creation](/img/explorer-add_criteria.png)

**3. Refine Criteria with Qualifiers**

To make your definition more precise, add Qualifiers to your criteria. These allow you to specify additional attributes of interest:

* **Occurrence:** Specify if an event occurred at least once (default), the first time, the latest time, or a specific number of times.

* **Recency:** Define a timeframe within which an encounter must have occurred.

* **Patient Age:** Set a requirement for the patient's age at the time of the encounter.

* **Provider Specialty:** For EHR Notes, you can specify the specialty of the provider who entered the note.

![Cohort Creation](/img/explorer-add_criteria_qualifiers.png)

**4. Combine and Link Criteria**

You can combine multiple criteria using **AND** or **OR** operators within blocks to create more complex cohort definitions. 

Temporal relationships between criteria can also be built using the **RELATE** operator. For example, you can find patients who were diagnosed with a condition within a certain number of months after starting a specific medication.

![Cohort Creation](/img/explorer-add_criteria_relations.png)

### [Code List Building](/cohort-builder/dashboard/custom/)

When you develop a cohort, you have to build code lists for a particular criteria. For all data elements, you can search by code as well as keyword matching. You can also paste a comma-delimited list of codes to search for all of them simultaneously. 

Available code type ontologies are listed in radio buttons underneath the search bar, and are described for each data element in more detail below. 

![Code List Building](/img/explorer-code_lists.png)

* **Diagnosis:** There are 3 code types available to search and select diagnoses. 

  * ***OM1 Codes:*** Code lists developed by the OM1 Research team. 

  * ***ICD10 Codes:*** ICD10 codes given directly from our data sources. 

  * ***GEMs-Mapped ICD10 Codes:*** ICD10 codes that are either (a) given directly by our data sources and (b) mapped from ICD9 codes using the CMS General Equivalence Mappings (GEMs). 

* **Medications:** There are 3 code types available to search and select medications. 

  * ***OM1 Codes:*** Code lists developed by the OM1 Research team. Contains both drugs as well as mechanisms of actions / drug classes.

  * ***Medication Classifications:*** Search and select drugs and common drug groupings defined by the Veteran's Affairs (VA) Drug Classification system.  

  * ***NDC Codes:*** Search and select by National Drug Codes (NDCs). 

* **Labs:** Lab results can be searched and selected by LOINC. There is also the option to select "Any lab" which will allow patients with any lab result to qualify.  

* **Procedures:** Procedures can be searched for and selected by CPT code. 

### [Patient Attrition Funnel](/cohort-builder/dashboard/custom/)

After a cohort definition is built, you can generate an attrition funnel for the criteria blocks by clicking on the funnel icon at top right. 

The attrition counts are listed on each criteria block, and percentages are calculated by dividing each count by the count on the first criteria block.

![Patient Attrition Funnel](/img/explorer-attrition.png)
`

// eslint-disable-next-line string-to-lingui/missing-lingui-transformation
const insightsLibraryContent = `# Insights Library

## Overview

Insights Library offers rapid, continuous insights on real-world treatments and outcomes via pre-built and custom analytics dashboards accessible through the OM1 Insights Platform. 

It provides access to pre-built, interactive analytics dashboards on real-world effectiveness, treatment patterns and provider insights. These dashboards can easily be configured based on the cohort, medications, medication groups and patient demographics of interest; and OM1 can also build custom dashboards that fit your specific needs. 

The analyses presented in these dashboards typically do not employ robust statistical methods such as population weighting. For RWE studies requiring more rigorous methodologies tailored to your specific requirements, the OM1 Research team offers custom study services. The underlying datasets are also available for licensing.

### Use Cases

* **Analyze Real-World Effectiveness:** Demonstrate the value and effectiveness of your medication by understanding real-world patient results and effectiveness . This includes leveraging PROs and other clinical outcomes extracted from unstructured notes.

* **Understand Prescribing Trends:** Understand the complexities of prescribing and provider patterns. This includes reconciling prescribing behaviors against real-world effectiveness and understanding how behaviors differ by clinician role and disease severity. Insights from unstructured clinical notes can help bypass traditional reliance on physician recall.

* **Understand Patterns in Discontinuation:** Gain comprehensive insights into why patients discontinue your medication (what we call "reasons for discontinuation" or  "RfD", which are extracted from unstructured clinical notes). The reasons for discontinuation overview shows distributions of RfD categories for different medication groups. Analysis can be stratified by medication and patient demographics. You can also analyze time to discontinuation.

* **Identify Under or Misdiagnosed Populations:** Identify under- or mis-diagnosed patient populations using PhenOM, OM1's proprietary AI model for predicting diagnoses. Link those insights with provider and site information to identify areas for commercial focus. 

## Workflows

### [Library Access](/insights-library)

Access the Insights Library by clicking on the chart icon in the navigation bar to the left. 

Each available report is listed on the page and grouped by condition. Additional reports are available on request. 

Click on "View Report" to access each report. 

![Insights Library](/img/insights_library.png)

### [Dashboard Filtering](/insights-library)

Each dashboard can be filtered on factors such as patient demographics, geography, medications, etc. 

To apply a filter, click on the white arrow on the right hand side to expand the filter pane. Then select which factors you want to filter on.

![Insights Library Filters](/img/insights_library-filters.png)`

// eslint-disable-next-line string-to-lingui/missing-lingui-transformation
const dataStudioContent = `# Data Studio

## Overview

Data Studio allows you to perform deep analysis on OM1 data without the need for data ingestion or compute infrastructure. 

It is a cloud-based analytics environment where OM1 can rapidly deliver data and your team can access quickly through a web interface. This allows you to query the data directly using familiar analysis tools and libraries like SQL, Python, R and their associated libraries. You can also easily create visualizations without code and collaborate with other team members within your organization. 

### Use Cases

* **Browse Dataset Schema and Variables:** View the schema and explore elements and variables that are available in the dataset. 

* **Evaluate Data:** More deeply evaluate datasets in a fully-featured data analysis environment. More fully analyze completeness and comprehensiveness of the data. 

* **Conduct RWE Research Studies:** To save time and effort of data ingestion, mapping and infrastructure setup, you can conduct your RWE study directly in Data Studio. OM1 can deliver datasets from Cohort Explorer within 1-2 days of a request. 

## Workflows

### Data Schema Browser

Click on the database icon on the left hand side to browse the dataset schema. 

Click on any of the tables to view the columns and example values. 

![Data Schema Browser](/img/data_studio-db_browser.png)

### New Analysis Project

Start a new analysis project by clicking the downfacing arrow next to the "New Project" button at top right. 

In the dropdown, click on "New project" to create a new analysis notebook that supports SQL and Python. For a notebook that supports R, click on the "New R project".

![Data Studio New Project](/img/data_studio-new_project.png)`

interface StyledMarkdownProps {
    children: string
    sx?: SxProps<Theme>
    imageMaxWidth?: string
}

const StyledMarkdown: React.FunctionComponent<StyledMarkdownProps> = ({ children, sx = {}, imageMaxWidth = '50%' }) => {
    return (
        <Box sx={sx}>
            <ReactMarkdown
                components={{
                    img: ({ src, alt, ...props }) => (
                        <img
                            src={src}
                            alt={alt}
                            style={{
                                maxWidth: imageMaxWidth || '50%',
                                height: 'auto',
                                margin: '24px auto',
                                display: 'block',
                                borderRadius: '10px',
                                border: '1px solid rgb(207, 207, 207)'
                            }}
                            loading='lazy'
                            {...props}
                        />
                    )
                }}
            >
                {children}
            </ReactMarkdown>
        </Box>
    )
}

interface TabPanelProps {
    children?: React.ReactNode
    index: number
    value: number
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props

    return (
        <div
            role='tabpanel'
            hidden={value !== index}
            id={`documentation-tabpanel-${index}`}
            aria-labelledby={`documentation-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ py: 0.5 }}>{children}</Box>}
        </div>
    )
}

export const DocumentationPage: React.FunctionComponent = () => {
    const [mainTabValue, setMainTabValue] = useState(0) // Start with Insights Platform
    const [platformTabValue, setPlatformTabValue] = useState(0)
    const [rwdcTabValue, setRwdcTabValue] = useState(0)
    const location = useLocation()

    // Handle URL parameters to set correct tab state
    useEffect(() => {
        const searchParams = new URLSearchParams(location.search)
        const section = searchParams.get('section')
        const subsection = searchParams.get('subsection')

        if (section) {
            switch (section) {
                case 'platform':
                    setMainTabValue(0)
                    if (subsection) {
                        switch (subsection) {
                            case 'getting-started':
                                setPlatformTabValue(0)
                                break
                            case 'cohort-explorer':
                                setPlatformTabValue(1)
                                break
                            case 'insights-library':
                                setPlatformTabValue(2)
                                break
                            case 'data-studio':
                                setPlatformTabValue(3)
                                break
                            default:
                                setPlatformTabValue(0)
                        }
                    }
                    break
                case 'rwdc':
                    setMainTabValue(1)
                    if (subsection) {
                        switch (subsection) {
                            case 'getting-started':
                                setRwdcTabValue(0)
                                break
                            case 'data-sources':
                                setRwdcTabValue(1)
                                break
                            case 'data-characteristics':
                                setRwdcTabValue(2)
                                break
                            default:
                                setRwdcTabValue(0)
                        }
                    }
                    break
                default:
                    // Default to Insights Platform Getting Started
                    setMainTabValue(0)
                    setPlatformTabValue(0)
            }
        }
    }, [location.search])

    const handleMainTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setMainTabValue(newValue)
    }

    const handlePlatformTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setPlatformTabValue(newValue)
    }

    const handleRwdcTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setRwdcTabValue(newValue)
    }

    const markdownStyles = {
        '& h1': {
            fontSize: '2rem',
            fontWeight: 600,
            color: 'primary.main',
            marginBottom: 1,
            marginTop: 2
        },
        '& h2': {
            fontSize: '1.5rem',
            fontWeight: 600,
            color: 'primary.main',
            marginBottom: 0.5,
            marginTop: 1.5
        },
        '& h3': {
            fontSize: '1.25rem',
            fontWeight: 600,
            marginBottom: 0.5,
            marginTop: 1.25
        },
        '& h4': {
            fontSize: '1.1rem',
            fontWeight: 600,
            marginBottom: 0.5,
            marginTop: 1
        },
        '& p': {
            marginBottom: 1,
            lineHeight: 1.6,
            marginTop: 0.5
        },
        '& ul': {
            marginBottom: 1,
            paddingLeft: 3,
            marginTop: 0.5
        },
        '& li': {
            marginBottom: 0.25
        },
        '& strong': {
            fontWeight: 600
        },
        '& em': {
            fontStyle: 'italic'
        },
        '& a': {
            color: 'primary.main',
            textDecoration: 'none',
            '&:hover': {
                textDecoration: 'underline'
            }
        }
    }

    return (
        <Container maxWidth={false} sx={{ maxWidth: '1400px', mx: 'auto', pt: 2, pb: 1, mb: 2, bgcolor: 'white', borderRadius: 2, boxShadow: 1 }}>
            {/* Breadcrumb Navigation */}
            <Box sx={{ mb: 2 }}>
                <Breadcrumbs separator='›' aria-label='breadcrumb'>
                    <BreadcrumbLink to={`/${Routes.HOME}`} sx={{ fontWeight: '600', fontSize: '16px', color: '#012D72' }}>
                        <Trans>Home</Trans>
                    </BreadcrumbLink>
                    <Box sx={{ fontWeight: '600', fontSize: '16px', color: '#707171' }}>
                        <Trans>Documentation</Trans>
                    </Box>
                </Breadcrumbs>
            </Box>

            {/* Main tabs */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}>
                <Tabs value={mainTabValue} onChange={handleMainTabChange} aria-label='main documentation tabs'>
                    <Tab
                        label={<Trans>Insights Platform</Trans>}
                        id='main-tab-0'
                        aria-controls='main-tabpanel-0'
                        sx={{ fontSize: '1rem', minHeight: '48px' }}
                    />
                    <Tab
                        label={<Trans>Real World Data Cloud</Trans>}
                        id='main-tab-1'
                        aria-controls='main-tabpanel-1'
                        sx={{ fontSize: '1rem', minHeight: '48px' }}
                    />
                </Tabs>
            </Box>

            {/* Insights Platform Tab Panel */}
            <TabPanel value={mainTabValue} index={0}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}>
                    <Tabs value={platformTabValue} onChange={handlePlatformTabChange} aria-label='platform documentation tabs'>
                        <Tab
                            label={<Trans>Getting Started</Trans>}
                            id='platform-tab-0'
                            aria-controls='platform-tabpanel-0'
                            sx={{ fontSize: '0.8rem', minHeight: '40px' }}
                        />
                        <Tab
                            label={<Trans>Cohort Explorer</Trans>}
                            id='platform-tab-1'
                            aria-controls='platform-tabpanel-1'
                            sx={{ fontSize: '0.8rem', minHeight: '40px' }}
                        />
                        <Tab
                            label={<Trans>Insights Library</Trans>}
                            id='platform-tab-2'
                            aria-controls='platform-tabpanel-2'
                            sx={{ fontSize: '0.8rem', minHeight: '40px' }}
                        />
                        <Tab
                            label={<Trans>Data Studio</Trans>}
                            id='platform-tab-3'
                            aria-controls='platform-tabpanel-3'
                            sx={{ fontSize: '0.8rem', minHeight: '40px' }}
                        />
                    </Tabs>
                </Box>

                <TabPanel value={platformTabValue} index={0}>
                    <StyledMarkdown sx={markdownStyles}>{gettingStartedContent}</StyledMarkdown>
                </TabPanel>

                <TabPanel value={platformTabValue} index={1}>
                    <StyledMarkdown sx={markdownStyles} imageMaxWidth='45%'>
                        {cohortExplorerContent}
                    </StyledMarkdown>
                </TabPanel>

                <TabPanel value={platformTabValue} index={2}>
                    <StyledMarkdown sx={markdownStyles} imageMaxWidth='45%'>
                        {insightsLibraryContent}
                    </StyledMarkdown>
                </TabPanel>

                <TabPanel value={platformTabValue} index={3}>
                    <StyledMarkdown sx={markdownStyles} imageMaxWidth='45%'>
                        {dataStudioContent}
                    </StyledMarkdown>
                </TabPanel>
            </TabPanel>

            {/* Real World Data Cloud Tab Panel */}
            <TabPanel value={mainTabValue} index={1}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1 }}>
                    <Tabs value={rwdcTabValue} onChange={handleRwdcTabChange} aria-label='rwdc documentation tabs'>
                        <Tab
                            label={<Trans>Getting Started</Trans>}
                            id='rwdc-tab-0'
                            aria-controls='rwdc-tabpanel-0'
                            sx={{ fontSize: '0.8rem', minHeight: '44px' }}
                        />
                        <Tab
                            label={<Trans>Data Sources</Trans>}
                            id='rwdc-tab-1'
                            aria-controls='rwdc-tabpanel-1'
                            sx={{ fontSize: '0.8rem', minHeight: '44px' }}
                        />
                        <Tab
                            label={<Trans>Data Characteristics</Trans>}
                            id='rwdc-tab-2'
                            aria-controls='rwdc-tabpanel-2'
                            sx={{ fontSize: '0.8rem', minHeight: '44px' }}
                        />
                    </Tabs>
                </Box>

                <TabPanel value={rwdcTabValue} index={0}>
                    <StyledMarkdown sx={markdownStyles} imageMaxWidth='55%'>
                        {rwdcGettingStartedContent}
                    </StyledMarkdown>
                </TabPanel>

                <TabPanel value={rwdcTabValue} index={1}>
                    <StyledMarkdown sx={markdownStyles}>{rwdcDataSourcesContent}</StyledMarkdown>
                </TabPanel>

                <TabPanel value={rwdcTabValue} index={2}>
                    <StyledMarkdown sx={markdownStyles}>{rwdcDataCharacteristicsContent}</StyledMarkdown>
                </TabPanel>
            </TabPanel>
        </Container>
    )
}
